import type { Mixins } from '@mui/material/styles/createMixins';
import { createBreakpoints } from '@mui/system';
import { breakpoints as breakpointsValues } from '../default/breakpoints';
import { gradientsDark, gradientsLight, IGradientsMixins } from './gradients';
import { colorsDark } from './colors';
import { CSSProperties } from 'react';
const breakpoints = createBreakpoints(breakpointsValues);

declare module '@mui/material/styles/createMixins' {
  interface Mixins extends IGradientsMixins {
    hoverTextLightBlue: CSSProperties;
    mobilePadding: (paddingY?: string) => CSSProperties;
    flexColumnStartStart: CSSProperties;
    defaultTransition: CSSProperties;
    companiesCarouselGradient: CSSProperties;
    shadowButtonMd: CSSProperties;
    gradientTable: (size: 'small' | 'regular' | 'large') => CSSProperties;
  }
}

const applyMobilePadding = (paddingY = '0px') => {
  return {
    [breakpoints.down('md')]: {
      padding: `${paddingY} 48px`,
    },
    [breakpoints.down('sm')]: {
      padding: `${paddingY} 24px`,
    },
  };
};

const mixinsTemplate: Mixins = {
  defaultTransition: {
    transition: 'all 0.3s ease-in-out',
  },
  //NEED TO CHANGE IT IN FUTURE
  hoverTextLightBlue: {
    transition: 'all 0.3s ease-in-out',
    color: '#2F57F4',
  },
  companiesCarouselGradient: {
    background:
      'linear-gradient(90deg, rgba(217, 217, 217, 0.00) 0%, rgba(255, 255, 255, 0.1) 50%, rgba(217, 217, 217, 0.00) 100%)',
  },
  shadowButtonMd: {
    boxShadow: '0px 8px 24px 0px rgba(48, 98, 212, 0.25)',
  },
  mobilePadding: applyMobilePadding,
  flexColumnStartStart: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
  },

  gradientTable: (size: 'small' | 'regular') => {
    const tdFontSize = {
      small: { xs: '12px', sm: '13px', md: '15px' },
      regular: { xs: '15px', sm: '16px', md: '18px' },
      large: { xs: '15px', sm: '16px', md: '18px' },
    };

    const thFontSize = {
      small: { xs: '14px', sm: '15px', md: '16px' },
      regular: { xs: '16px', sm: '18px', md: '20px' },
      large: { xs: '18px', sm: '20px', md: '22px' },
    };

    const verticalPadding = {
      small: { xs: '8px', sm: '8px', md: '12px' },
      regular: { xs: '8px', sm: '12px', md: '12px' },
      large: { xs: '8px', sm: '12px', md: '24px' },
    };

    const horizontalPadding = {
      small: { xs: '5px', sm: '6px', md: '10px' },
      regular: { xs: '8px', sm: 'px', md: '8px' },
      large: { xs: '8px', sm: '8px', md: '8px' },
    };

    return {
      '& table': {
        margin: 0,
        width: '100%',
        borderRadius: '36px',

        backgroundImage: `linear-gradient(to right, ${colorsDark.blueColors?.PLUS_1}, ${colorsDark.aquamarineColors?._0})` /* the gradient */,
        backgroundOrigin:
          'border-box' /* set background to start from border-box */,
        borderSpacing: '2px' /* space between each cell */,
      },

      '& th, & td': {
        padding: `${verticalPadding[size].md} ${horizontalPadding[size].md}`,
        textAlign: 'center',
        background: '#070E15',
        fontSize: tdFontSize[size].md,
        lineHeight: 1.5,
        color: colorsDark.invertedContent.PRIMARY,

        [breakpoints.down('md')]: {
          fontSize: tdFontSize[size].sm,
          padding: `${verticalPadding[size].sm} ${horizontalPadding[size].sm}`,
        },

        [breakpoints.down('sm')]: {
          fontSize: tdFontSize[size].xs,
          padding: `${verticalPadding[size].xs} ${horizontalPadding[size].xs}`,
        },
      },

      '& th': {
        fontWeight: 700,
        color: colorsDark.invertedContent?.PRIMARY,
        fontSize: thFontSize[size].md,

        [breakpoints.down('md')]: {
          fontSize: thFontSize[size].sm,
        },

        [breakpoints.down('sm')]: {
          fontSize: thFontSize[size].xs,
        },
      },

      '& tr:first-child > th:first-child': {
        borderTopLeftRadius: '36px',
      },
      '& tr:first-child > th:last-child': {
        borderTopRightRadius: '36px',
      },
      '& tr:last-child td:first-child': {
        borderBottomLeftRadius: '36px',
      },
      '& tr:last-child td:last-child': {
        borderBottomRightRadius: '36px',
      },
    } as CSSProperties;
  },
} as Mixins;

export const mixinsDark: Mixins = {
  ...mixinsTemplate,
  ...gradientsDark,
} as Mixins;
export const mixinsLight: Mixins = {
  ...mixinsTemplate,
  ...gradientsLight,
} as Mixins;
